import React, { useEffect, useState } from 'react';
import RialSymbol from '../components/RialSymbol';
import {
  Box, Card, Chip, FormControl, InputAdornment,
  InputLabel, MenuItem, Paper, Select, SelectChangeEvent, Table,
  TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Typography,
} from '@mui/material';
import {
  Search as SearchIcon,
  TrendingUp as TrendingIcon,
} from '@mui/icons-material';
import axios from '../api';
import PaginationControls from '../components/PaginationControls';

export default function Payments() {
  const [payments, setPayments] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [serverStats, setServerStats] = useState<{ total: number; completed: number; pending: number; failed: number; revenue: number } | null>(null);

  useEffect(() => {
    fetchPayments();
  }, [page, rowsPerPage]);

  const fetchPayments = async () => {
    try {
      const response = await axios.get('/api/operations/payments', { params: { page, limit: rowsPerPage } });
      const paymentRecords = response.data.payments.map((order: any) => ({
        _id: order._id,
        orderNumber: order.orderNumber,
        amount: order.pricing?.total || 0,
        method: order.payment?.method || 'card',
        status: order.payment?.status || 'pending',
        customer: order.customer?.name || 'Unknown',
        date: order.createdAt,
        transactionId: order.payment?.transactionId || 'N/A',
      }));

      setPayments(paymentRecords);
      setTotal(response.data.total ?? paymentRecords.length);
      setServerStats(response.data.stats || null);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching payments:', error);
      setLoading(false);
    }
  };

  const filteredPayments = payments.filter((payment) => {
    const matchesSearch = payment.orderNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      payment.customer?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'All' || payment.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  // Prefer the backend's platform-wide counts (the API already computes
  // these via aggregate) so the cards don't undercount once there's more
  // than one page of payments.
  const stats = serverStats || {
    total: payments.length,
    completed: payments.filter(p => p.status === 'completed').length,
    pending: payments.filter(p => p.status === 'pending').length,
    failed: payments.filter(p => p.status === 'failed').length,
    revenue: payments.filter(p => p.status === 'completed').reduce((sum, p) => sum + p.amount, 0),
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>Payment Management</Typography>
          <Typography color="text.secondary">Monitor and manage all payment transactions.</Typography>
        </Box>
      </Box>

      {/* Stats */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 2, mb: 3 }}>
        <Paper sx={{ p: 2, textAlign: 'center' }}>
          <Typography color="text.secondary" variant="body2">Total Transactions</Typography>
          <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.main' }}>{stats.total}</Typography>
        </Paper>
        <Paper sx={{ p: 2, textAlign: 'center' }}>
          <Typography color="text.secondary" variant="body2">Completed</Typography>
          <Typography variant="h5" sx={{ fontWeight: 700, color: 'success.main' }}>{stats.completed}</Typography>
        </Paper>
        <Paper sx={{ p: 2, textAlign: 'center' }}>
          <Typography color="text.secondary" variant="body2">Pending</Typography>
          <Typography variant="h5" sx={{ fontWeight: 700, color: 'warning.main' }}>{stats.pending}</Typography>
        </Paper>
        <Paper sx={{ p: 2, textAlign: 'center' }}>
          <Typography color="text.secondary" variant="body2">Failed</Typography>
          <Typography variant="h5" sx={{ fontWeight: 700, color: 'error.main' }}>{stats.failed}</Typography>
        </Paper>
      </Box>

      <Paper sx={{ p: 2, textAlign: 'center', mb: 3, bgcolor: 'success.light', color: 'white' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 1 }}>
          <TrendingIcon />
          <Typography>Total Revenue</Typography>
        </Box>
        <Typography variant="h4" sx={{ fontWeight: 700 }}><RialSymbol /> {stats.revenue.toFixed(2)}</Typography>
      </Paper>

      <Card>
        <Box sx={{ display: 'flex', gap: 2, p: 2, flexWrap: 'wrap' }}>
          <TextField
            size="small"
            placeholder="Search by order or customer..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ minWidth: { xs: '100%', sm: 300 } }}
            slotProps={{ input: { startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> } }}
          />
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Status</InputLabel>
            <Select value={statusFilter} label="Status" onChange={(e: SelectChangeEvent) => setStatusFilter(e.target.value)}>
              {['All', 'completed', 'pending', 'failed'].map((opt) => (
                <MenuItem key={opt} value={opt}>{opt.charAt(0).toUpperCase() + opt.slice(1)}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'background.default' }}>
                <TableCell><strong>Order ID</strong></TableCell>
                <TableCell><strong>Customer</strong></TableCell>
                <TableCell><strong>Amount</strong></TableCell>
                <TableCell><strong>Method</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell><strong>Date</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 5 }}>
                    <Typography>Loading payments...</Typography>
                  </TableCell>
                </TableRow>
              )}
              {!loading && filteredPayments.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 5 }}>
                    <Typography color="text.secondary">No payments found.</Typography>
                  </TableCell>
                </TableRow>
              )}
              {!loading && filteredPayments.map((payment) => (
                <TableRow key={payment._id} hover>
                  <TableCell><Typography sx={{ fontWeight: 600 }}>{payment.orderNumber}</Typography></TableCell>
                  <TableCell>{payment.customer}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}><RialSymbol /> {payment.amount.toFixed(2)}</TableCell>
                  <TableCell>
                    <Chip label={payment.method} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={payment.status}
                      color={payment.status === 'completed' ? 'success' : payment.status === 'failed' ? 'error' : 'warning'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{new Date(payment.date).toLocaleDateString()}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
      <PaginationControls page={page} total={total} rowsPerPage={rowsPerPage} onPageChange={setPage} onRowsPerPageChange={(value) => { setRowsPerPage(value); setPage(1); }} />
    </Box>
  );
}
